<template>
  <v-col cols="12" md="6" lg="6" class="mb-0 pb-0">
    <v-row>
      <!-- Stallholder Select -->
      <v-col cols="12" lg="5" md="6" sm="6">
        <v-autocomplete
          v-model="selectedStallholder"
          :items="allStallHoldersList"
          :label="labelStallHolders"
          item-text="StallholderName"
          return-object
          filled
          dense
          outlined
          hide-details
          class="mb-0"
          @change="handleStallholderChange"
        >
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-title>
                <v-icon small color="red lighten-1"> mdi-information </v-icon>
                No stallholders found
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-autocomplete>
      </v-col>

      <!-- Stalls Select (only shown when showStalls prop is true) -->
      <v-col v-if="showStalls" cols="12" lg="5" md="6" sm="6">
        <v-autocomplete
          v-model="selectedStall"
          :items="stallsList"
          :label="labelStalls"
          item-text="StallName"
          return-object
          filled
          dense
          outlined
          hide-details
          class="mb-0"
          :disabled="!selectedStallholder || stallsList.length === 0"
          @change="handleStallChange"
        >
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-title>
                <v-icon small color="red lighten-1"> mdi-information </v-icon>
                {{
                  selectedStallholder
                    ? "No stalls found for this stallholder"
                    : "Select a stallholder first"
                }}
              </v-list-item-title>
            </v-list-item>
          </template>
        </v-autocomplete>
      </v-col>
    </v-row>
  </v-col>
</template>

<script>
import { mapState, mapMutations } from "vuex";

export default {
  name: "StallholderSingleSelect",
  props: {
    showStalls: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState("filter", ["globalEventFilter"]),
    ...mapState("admin", [
      "currentStallholder",
      "currentStall",
      "currentStallHolders",
    ]),

    allStallHoldersList() {
      return this.globalEventFilter?.Stallholders || [];
    },

    stallsList() {
      // Get fresh stalls data from currentStallHolders to ensure reactivity
      if (this.selectedStallholder && this.currentStallHolders) {
        const freshStallholder = this.currentStallHolders.find(
          (sh) => sh.StallholderId === this.selectedStallholder.StallholderId
        );
        return freshStallholder?.Stalls || [];
      }
      return this.selectedStallholder?.Stalls || [];
    },

    selectedStallholder: {
      get() {
        return this.currentStallholder;
      },
      set(value) {
        this.setCurrentStallholder(value);
      },
    },

    selectedStall: {
      get() {
        return this.currentStall;
      },
      set(value) {
        this.setCurrentStall(value);
      },
    },
  },
  data() {
    return {
      labelStallHolders: "Stallholder",
      labelStalls: "Stall",
    };
  },
  watch: {
    // Handle stall selection when stallholder changes
    selectedStallholder(newStallholder, oldStallholder) {
      if (this.showStalls && newStallholder !== oldStallholder) {
        // Only handle selection if the stallholder ID actually changed
        const stallholderIdChanged =
          !oldStallholder ||
          !newStallholder ||
          oldStallholder.StallholderId !== newStallholder.StallholderId;

        if (stallholderIdChanged && newStallholder) {
          // Auto-select the first stall if available
          const availableStalls = newStallholder.Stalls || [];
          if (availableStalls.length > 0) {
            this.selectedStall = availableStalls[0];
          } else {
            this.selectedStall = null;
          }
        }
      }
    },

    // Watch for updates to currentStallHolders (when getEventMetadata is called)
    currentStallHolders: {
      handler(newStallHolders) {
        if (this.showStalls && this.selectedStallholder && newStallHolders) {
          // Find the updated stallholder in the new array
          const updatedStallholder = newStallHolders.find(
            (sh) => sh.StallholderId === this.selectedStallholder.StallholderId
          );

          if (updatedStallholder) {
            // Update the current stallholder with the fresh data
            this.setCurrentStallholder(updatedStallholder);

            // If we have a current stall selected, try to find it in the updated data
            if (this.selectedStall && this.selectedStall.StallId) {
              const updatedStall = updatedStallholder.Stalls?.find(
                (s) => s.StallId === this.selectedStall.StallId
              );
              if (updatedStall) {
                // Update the current stall with fresh data
                this.setCurrentStall(updatedStall);
              }
            } else if (
              !this.selectedStall &&
              updatedStallholder.Stalls?.length > 0
            ) {
              // If no stall is currently selected but stalls are available, select the first one
              this.setCurrentStall(updatedStallholder.Stalls[0]);
              this.$emit("stall-changed", updatedStallholder.Stalls[0]);
            }
          }
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapMutations("admin", ["setCurrentStallholder", "setCurrentStall"]),

    handleStallholderChange(stallholder) {
      if (stallholder) {
        // Check if this is actually a different stallholder
        const stallholderIdChanged =
          !this.selectedStallholder ||
          this.selectedStallholder.StallholderId !== stallholder.StallholderId;

        this.setCurrentStallholder(stallholder);
        this.$emit("stallholder-changed", stallholder);

        // When stallholder changes and showStalls is enabled
        if (this.showStalls && stallholderIdChanged) {
          // Auto-select the first stall if available
          const availableStalls = stallholder.Stalls || [];
          if (availableStalls.length > 0) {
            this.setCurrentStall(availableStalls[0]);
            this.$emit("stall-changed", availableStalls[0]);
          } else {
            // Clear stall selection if no stalls available
            this.setCurrentStall(null);
          }
        }
      }
    },

    handleStallChange(stall) {
      if (stall) {
        this.setCurrentStall(stall);
        this.$emit("stall-changed", stall);
      }
    },
  },
};
</script>

<style scoped>
/* Remove only bottom spacing */
.v-autocomplete {
  margin-bottom: 0 !important;
}

/* Target the input slot for bottom spacing */
.v-autocomplete .v-input__slot {
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

/* Target the text field wrapper for bottom spacing */
.v-autocomplete .v-text-field__details {
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

/* Remove bottom spacing from form elements */
.v-input {
  margin-bottom: 0 !important;
}

.v-text-field {
  margin-bottom: 0 !important;
}
</style>
